/**
 * Validation Step (Step 3)
 * Validates the broker session and connection
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Security,
  ApiOutlined,
} from "@mui/icons-material";
import { Icon } from "../../../../components/ds";
import { services } from "../../../../services/api";
import LoadingSpinner from "../../Components/LoadingSpinner";
import AlertMessage from "../../Components/AlertMessage";

const ValidationStep = ({
  accountData,
  selectedBroker,
  onValidationSuccess,
  onBack,
  loading: parentLoading,
}) => {
  // Component state
  const [loading, setLoading] = useState(false);
  const [validationStatus, setValidationStatus] = useState("pending"); // pending, validating, success, error
  const [validationResults, setValidationResults] = useState({});
  const [error, setError] = useState("");
  const [validationDetails, setValidationDetails] = useState([]);

  // Start validation automatically
  useEffect(() => {
    if (accountData?.account_id) {
      startValidation();
    }
  }, [accountData]);

  // Start validation process
  const startValidation = async () => {
    try {
      setLoading(true);
      setValidationStatus("validating");
      setError("");
      console.log("Submitting account validation for account:", accountData);
      const response = await services.brokerAccounts.validateAccount(accountData.account_id);
      console.log("Validation response:", response);
      
      // Check if the API call was successful (HTTP 200)
      if (response.status === 200 && response.data.status === "success") {
        const validationData = response.data.data;
        setValidationResults(validationData);
        
        // Create validation details from the response data
        const details = [
          {
            check: "broker_connection",
            title: "Broker Connection",
            message: response.data.message || "Connection established successfully",
            status: "success"
          },
          {
            check: "account_verification",
            title: "Account Verification",
            message: `Account verified for ${validationData.broker_user_name} (ID: ${validationData.broker_user_id})`,
            status: validationData.status === "verified" ? "success" : "error"
          }
        ];
        setValidationDetails(details);

        // Check if validation was successful
        if (validationData.status === "verified") {
          setValidationStatus("success");
          // Auto-proceed after showing success for a moment
          setTimeout(() => {
            onValidationSuccess();
          }, 2000);
        } else {
          setValidationStatus("error");
          setError(`Account validation failed: ${validationData.status}`);
        }
      } else {
        throw new Error(response.data?.message || "Validation request failed");
      }
    } catch (err) {
      setValidationStatus("error");
      setError(`Validation failed: ${err.message}`);
      console.error("Validation error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Retry validation
  const handleRetry = () => {
    setValidationStatus("pending");
    setError("");
    setValidationResults({});
    setValidationDetails([]);
    startValidation();
  };

  // Render validation check item
  const renderValidationItem = (item) => {
    const getIcon = () => {
      switch (item.status) {
        case "success":
          return <Icon name="success" />;
        case "error":
          return <Icon name="error" />;
        default:
          return <Icon name="refresh" />;
      }
    };

    return (
      <ListItem key={item.check}>
        <ListItemIcon>{getIcon()}</ListItemIcon>
        <ListItemText
          primary={item.title}
          secondary={item.message}
          primaryTypographyProps={{
            color: item.status === "error" ? "error.main" : "text.primary",
          }}
        />
      </ListItem>
    );
  };

  // Render status indicator
  const renderStatusIndicator = () => {
    switch (validationStatus) {
      case "pending":
        return (
          <span className="ds-badge sm neutral">
            <Icon name="refresh" size={13} />
            Ready to Validate
          </span>
        );
      case "validating":
        return (
          <span className="ds-badge sm info">
            <Icon name="refresh" size={13} />
            Validating...
          </span>
        );
      case "success":
        return (
          <span className="ds-badge sm profit">
            <Icon name="success" size={13} />
            Validation Successful
          </span>
        );
      case "error":
        return (
          <span className="ds-badge sm loss">
            <Icon name="error" size={13} />
            Validation Failed
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Validate Connection
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        We're validating your broker connection to ensure everything is working
        correctly.
      </Typography>

      {/* Error Message */}
      {error && (
        <AlertMessage
          severity="error"
          title="Validation Error"
          message={error}
          sx={{ mb: 3 }}
        />
      )}

      {/* Loading State */}
      {(loading || parentLoading) && validationStatus === "validating" ? (
        <LoadingSpinner message="Validating your broker connection..." />
      ) : (
        <Box>
          {/* Validation Status Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                {selectedBroker?.logo && (
                  <img
                    src={selectedBroker.logo}
                    alt={selectedBroker.name}
                    style={{ width: 32, height: 32 }}
                  />
                )}
                <Typography variant="h6">{selectedBroker?.name}</Typography>
                {renderStatusIndicator()}
              </Box>

              <Typography variant="body2" color="text.secondary">
                Account: {validationResults?.broker_user_name || accountData?.account_name}
                {validationResults?.broker_user_id && (
                  <span> (ID: {validationResults.broker_user_id})</span>
                )}
              </Typography>
            </CardContent>
          </Card>

          {/* Validation Progress/Results */}
          {validationStatus === "validating" && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Validation in Progress...
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <LoadingSpinner size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Checking connection and permissions...
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Validation Results */}
          {validationDetails.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Validation Results
                </Typography>
                <List dense>{validationDetails.map(renderValidationItem)}</List>
              </CardContent>
            </Card>
          )}

          {/* Success State */}
          {validationStatus === "success" && (
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Icon name="success" size={60} style={{ marginBottom: 16 }} />
              <Typography variant="h6" color="success.main" gutterBottom>
                Validation Successful!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your broker account is connected and ready to use.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Proceeding to completion...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {validationStatus === "error" && (
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Icon name="error" size={60} style={{ marginBottom: 16 }} />
              <Typography variant="h6" color="error.main" gutterBottom>
                Validation Failed
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                There was an issue validating your connection. Please check your
                credentials and try again.
              </Typography>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleRetry}
              >
                <Icon name="refresh" size={16} />
                Retry Validation
              </button>
            </Box>
          )}

          {/* Pending State */}
          {validationStatus === "pending" && (
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={startValidation}
              >
                <Security sx={{ fontSize: 16 }} />
                Start Validation
              </button>
            </Box>
          )}

          {/* Navigation Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onBack}
              disabled={validationStatus === "validating"}
            >
              Back
            </button>

            {/* Success case automatically proceeds */}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ValidationStep;
